/**
 * The pipeline editor's client. CRM-BUILD, added 2026-09-15.
 *
 * ⚠️ `import type` FROM THE SERVICE, NOT A VALUE IMPORT. `pipeline-editor.ts`
 * imports the database driver, and a value import from it would take Postgres
 * into the browser bundle - measured once at 98 modules to 225 and caught now by
 * `ops/check-bundle-boundary.sh`.
 */
import { reportUnauthorized } from './unauthorized.js'
import type { PipelineDetail, Stage, StageKind } from '../../../src/services/pipeline-editor.js'

export type EditorPipeline = PipelineDetail
export type EditorStage = Stage
export type { StageKind }

export type StagePayload = { id?: string; label: string; kind: StageKind; colour?: string | null }

/**
 * ⛔ A REFUSAL IS A VALUE HERE TOO, CARRYING ITS NUMBERS. The dialog renders the
 * sentence, so the reason code and its count have to survive the wire - an
 * error string could not be counted or pluralised.
 */
export type EditOutcome = { ok: true; id?: string } | { ok: false; reason: string; count?: number; kind?: string }

async function send(url: string, method: string, body?: unknown): Promise<EditOutcome> {
  const res = await fetch(url, {
    method,
    credentials: 'same-origin',
    ...(body === undefined
      ? {}
      : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  })
  if (res.status === 401) {
    reportUnauthorized()
    return { ok: false, reason: 'unauthorized' }
  }
  if (res.ok) {
    const parsed = (await res.json().catch(() => ({}))) as { id?: string }
    return parsed.id === undefined ? { ok: true } : { ok: true, id: parsed.id }
  }
  /** ⚠️ 409 CARRIES THE STRUCTURED REFUSAL; anything else is a shape this build
   *  does not know, and saying so beats rendering an empty sentence. */
  const parsed = (await res.json().catch(() => ({}))) as { reason?: string; count?: number; kind?: string }
  return {
    ok: false,
    reason: parsed.reason ?? (res.status === 404 ? 'not-found' : 'unknown'),
    ...(parsed.count === undefined ? {} : { count: parsed.count }),
    ...(parsed.kind === undefined ? {} : { kind: parsed.kind }),
  }
}

export async function fetchPipelines(): Promise<EditorPipeline[]> {
  const res = await fetch('/api/pipelines', { credentials: 'same-origin' })
  if (res.status === 401) {
    reportUnauthorized()
    return []
  }
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  return ((await res.json()) as { pipelines: EditorPipeline[] }).pipelines
}

/** ⛔ The stages travel WITH the create, because the deferred trigger demands a
 *  won and a lost at COMMIT and a two-step create could not satisfy it. */
export async function createPipeline(label: string, stages: StagePayload[]): Promise<EditOutcome> {
  return await send('/api/pipelines', 'POST', { label, stages })
}

export async function renamePipeline(id: string, label: string): Promise<EditOutcome> {
  return await send(`/api/pipelines/${id}`, 'PATCH', { label })
}

export async function saveStages(
  id: string,
  stages: StagePayload[],
  moveOrphansTo?: string,
): Promise<EditOutcome> {
  return await send(`/api/pipelines/${id}/stages`, 'PUT', {
    stages,
    ...(moveOrphansTo === undefined ? {} : { moveOrphansTo }),
  })
}

export async function deletePipeline(id: string): Promise<EditOutcome> {
  return await send(`/api/pipelines/${id}`, 'DELETE')
}
